import { BlueprintEmptyState, CheckMotif } from "@components/EmptyState";
import { useSnapshot, useSnapshotDrc } from "@features/circuits/hooks/useCircuitIr";
import type { DrcSeverity } from "@models/circuit-ir";
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useDraftStore } from "@stores/draftStore";
import { useMemo } from "react";

import { DrcFindingItem } from "./DrcFindingItem";
import { SEVERITY_LABEL } from "./DrcSeverityIcon";

interface DrcFindingsDrawerProps {
  snapshotId: string | null;
}

/**
 * DRC findings panel for the active draft snapshot. Per IR plan §6.7,
 * the validator returns EVERY finding tagged with severity; the
 * presentation layer is the one that filters. `useSnapshotDrc` fetches
 * unfiltered; tab switching is in-memory.
 *
 * The "all" tab is the default — leaving the agent / user with the full
 * picture and avoiding the "I told the model to suppress info-level so
 * now my errors are missing" footgun the IR plan calls out.
 */
export function DrcFindingsDrawer({ snapshotId }: DrcFindingsDrawerProps) {
  const { drcFilter, setDrcFilter } = useDraftStore();
  const { data: report, isLoading } = useSnapshotDrc(snapshotId);
  const { data: snapshot } = useSnapshot(snapshotId);

  const filtered = useMemo(() => {
    if (!report) return [];
    if (drcFilter === "all") return report.findings;
    return report.findings.filter((f) => f.severity === drcFilter);
  }, [report, drcFilter]);

  if (!snapshotId) {
    return (
      <Box sx={{ p: 2, color: "text.secondary" }}>
        <Typography variant="body2">
          Select a snapshot to inspect its DRC findings.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box sx={{ px: 1.5, pt: 1 }}>
        <Tabs
          value={drcFilter}
          onChange={(_, value) => setDrcFilter(value as DrcSeverity | "all")}
          variant="scrollable"
          allowScrollButtonsMobile
        >
          <Tab
            label={`All${report ? ` (${report.findings.length})` : ""}`}
            value="all"
          />
          <Tab
            label={`${SEVERITY_LABEL.error}${
              report ? ` (${report.error_count})` : ""
            }`}
            value="error"
          />
          <Tab
            label={`${SEVERITY_LABEL.warning}${
              report ? ` (${report.warning_count})` : ""
            }`}
            value="warning"
          />
          <Tab
            label={`${SEVERITY_LABEL.info}${
              report ? ` (${report.info_count})` : ""
            }`}
            value="info"
          />
        </Tabs>
      </Box>
      <Divider />

      {report?.has_errors && drcFilter !== "error" && (
        <Alert severity="error" sx={{ m: 1.5 }}>
          {report.error_count} hard error{report.error_count === 1 ? "" : "s"}{" "}
          will block commit. Switch to the Errors tab to see them.
        </Alert>
      )}

      <Box sx={{ flex: 1, overflowY: "auto" }}>
        {isLoading && (
          <Stack sx={{ p: 4, alignItems: "center" }}>
            <CircularProgress size={24} />
          </Stack>
        )}

        {!isLoading && filtered.length === 0 && (
          <Box sx={{ p: 2 }}>
            {report && report.findings.length > 0 ? (
              <Box sx={{ p: 3, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  No findings at this severity.
                </Typography>
              </Box>
            ) : (
              <BlueprintEmptyState
                motif={CheckMotif}
                eyebrow="DESIGN VERIFIED"
                headline="No DRC findings"
                body="Every structural check passed — orphan components, dangling nets, floating IC power pins, missing values. Ready to commit."
              />
            )}
          </Box>
        )}

        <Stack divider={<Divider flexItem />}>
          {filtered.map((f, i) => (
            <DrcFindingItem
              key={`${f.code}-${i}`}
              finding={f}
              ir={snapshot?.ir}
              snapshotId={snapshotId}
            />
          ))}
        </Stack>
      </Box>
    </Box>
  );
}
